import type { ReactNode } from 'react';

/** The four icons of the KPI row, traced from the approved mockup. */
export const StatIcons = {
  people: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  ),
  envelope: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m2 6 10 7L22 6" />
    </svg>
  ),
  gauge: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden>
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 4v5h-5" />
    </svg>
  ),
  approved: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
} as const;

type Props = {
  icon: ReactNode;
  label: string;
  value: string | number;
  /** One line of context under the number. Never a second competing figure. */
  detail?: string;
  /** The tile the eye should land on first. */
  accent?: boolean;
};

/**
 * A headline number with its icon.
 *
 * A stat tile, not a one-bar chart: a single current value has no magnitude to
 * compare against, so a bar would be ink without information. The icon is a
 * recognition aid, which is why it sits in a quiet tint rather than competing
 * with the number for attention.
 */
export const StatTile = ({ icon, label, value, detail, accent = false }: Props) => (
  <div className="flex items-center gap-4 rounded-2xl border border-line bg-paper px-5 py-4 shadow-card">
    <span className="grid size-13 shrink-0 place-items-center rounded-xl bg-tint text-teal [&>svg]:size-6 [&>svg]:stroke-current">
      {icon}
    </span>

    <div className="min-w-0">
      <p className="text-[12.5px] text-soft">{label}</p>
      <p
        className={[
          'mt-0.5 text-[32px] leading-none font-extrabold tracking-[-0.03em] tabular-nums',
          accent ? 'text-teal' : 'text-ink',
        ].join(' ')}
      >
        {value}
      </p>
      {detail && <p className="mt-1 text-[11.5px] leading-[1.3] text-faint">{detail}</p>}
    </div>
  </div>
);
